import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Accordion, Paper } from '@material-ui/core';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import Button from 'Components/UI/Button';
import Typography from 'Components/UI/Typography';
import MetaTable from 'Components/UI/Meta/MetaTable';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import { copyToClipboard } from 'Helpers/Utils';

const styles = theme => ({
  root: {
    height: 'auto',
  },
  cell: {
    marginTop: 0,
    fontSize: 12,
    textAlign: 'left',
    flexGrow: 1,
    width: '100%',
    maxWidth: '1/6',
    color: theme.palette.primary.main,
  },
  paper: {
    borderRadius: theme.radius.sm,
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  grey: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  info: {
    display: 'flex',
  },
  infoLabel: {
    minWidth: '300px',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 16,
  },
  head: {
    '& th': {
      backgroundColor: theme.palette.common.white,
    },
  },
  copyIdButton: {
    paddingLeft: 0,
  },
  submitButton: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
  },
});
const SubmitSupportTicketButton = ({ resource, className, ...rest }) => {
  if (resource.relationship('supportTicketUrl')) {
    return (
      <Tooltip title={'View support ticket in a new tab'}>
        <Button
          size="small"
          color={'primary'}
          variant="contained"
          {...rest}
        >
          {'View Support Ticket'}
        </Button>
      </Tooltip>
    );
  }

  if (resource.relationship('submitSupportTicket')) {
    return (
      <Tooltip title={'Submit a case to Plaid Support'}>
        <Button
          size="small"
          color={'secondary'}
          variant="contained"
          {...rest}
        >
          {'Open Support Case'}
        </Button>
      </Tooltip>
    );
  }

  if (resource.attr('noSupportExplanation')) {
    return (
      <Tooltip title={resource?.attr('noSupportExplanation')}>
        <div>
          <Button
            size="small"
            color={'secondary'}
            variant="contained"
            disabled
            fullWidth
          >
            {'Open Support Case'}
          </Button>
        </div>
      </Tooltip>
    );
  }

  return null;
};

const AccountSupportAccordion = ({
  className, classes, resource,
}) => {
  const [open, setOpen] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (resource.relationship('supportTicketUrl')) {
      window.open(resource.relationship('supportTicketUrl').link('related').url(), '_blank');
      return;
    }

    window.open('https://dashboard.plaid.com/support/new/financial-institutions', '_blank');
  };

  return (
    <Accordion
      square
      className={classNames(classes.root, className, {
        [classes.grey]: open,
      })}
    >
      <AccordionSummary
        onClick={() => setOpen(!open)}
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
          expanded: classes.expanded,
        }}
      >
        <div className={classes.cell}>{moment(resource.attr('date')).format('M/D/YY')}</div>
        <div className={classes.cell}>{resource.attr('institutionName')}</div>
        <div className={classes.cell}>{'Plaid'}</div>
        <div className={classes.cell}>{resource.attr('errorType')}</div>
        <div className={classes.cell}>{resource.attr('errorMessage')}</div>
        <div className={classes.cell}><SubmitSupportTicketButton
          resource={resource}
          className={classes.submitButton}
          onClick={handleSubmit}
          fullWidth
        />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className={classes.content}>
          <Paper className={classes.paper}>
            <div className={classes.info}>
              <Typography className={classes.infoLabel}>
                {'Source'}
              </Typography>
              <Typography>
                {'Plaid'}
              </Typography>
            </div>
            <div className={classes.info}>
              <Typography className={classes.infoLabel}>
                {'Status'}
              </Typography>
              <Typography color={resource.attr('status') === 'ERROR' ? 'orange' : 'secondary'}>
                {resource.attr('status')}
              </Typography>
            </div>
            <div className={classes.info}>
              <Typography className={classes.infoLabel}>
                {'Session ID'}
              </Typography>
              <Button endIcon={<FontAwesome icon="clipboard" />} className={classes.copyIdButton} onClick={() => copyToClipboard(resource.attr('linkSessionId'))}>
                {resource.attr('linkSessionId')}
              </Button>
            </div>
          </Paper>
          <Paper className={classes.paper}>
            <MetaTable
              collection={resource.attr('events')}
              tableMetaOverrideSetName="AccountSupportTableOverride"
              features={{}}
              HeadRowProps={{
                className: classes.head,
              }}
              columns={[
                { name: 'institution_name' },
                { name: 'eventName' },
                { name: 'error_code' },
                { name: 'error_type' },
                { name: 'error_message', style: { minWidth: '300px' } },
              ]}
            />
          </Paper>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

AccountSupportAccordion.defaultProps = {
  className: '',
};

AccountSupportAccordion.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};

export default withStyles(styles)(AccountSupportAccordion);
